import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './Contact.css';

const Contact: React.FC = () => {
  const form = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: ''
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (form.current) {
      setFormStatus({ submitted: false, error: false, message: 'Sending your message...' });
      
      // Use the provided EmailJS credentials
      emailjs.sendForm(
        'service_l09crg6', // Your EmailJS service ID
        'template_qdu65jj', // Your EmailJS template ID for admin notification
        form.current,
        'WSVr9WuU7rI-NrhiM' // Your EmailJS public key
      )
        .then((result) => {
          console.log('Email to admin sent successfully!', result.text);
          
          // Send auto-reply to the user with your specific template ID
          const autoReplyTemplateParams = {
            to_name: formData.name,
            to_email: formData.email,
            subject_line: `Re: ${formData.subject}`,
          };
          
          return emailjs.send(
            'service_l09crg6', 
            'template_lckgxfn', // Your auto-reply template ID
            autoReplyTemplateParams,
            'WSVr9WuU7rI-NrhiM'
          );
        })
        .then((result) => {
          console.log('Auto-reply sent successfully!', result.text);
          setFormStatus({
            submitted: true,
            error: false,
            message: 'Your message has been sent successfully!'
          });
          // Reset form after submission
          setFormData({
            name: '',
            email: '',
            subject: '',
            message: ''
          });
        })
        .catch((error) => {
          console.error('Failed to send email:', error.text);
          setFormStatus({
            submitted: false,
            error: true,
            message: 'Failed to send your message. Please try again later.'
          });
        });
    }
  };

  return (
    <section className="contact-page">
      <div className="contact-header">
        <h1>Get In Touch</h1>
        <div className="header-underline"></div>
        <p>Have questions about the Finance Club? We're here to help.</p>
      </div>
      
      <div className="contact-container">
        <div className="contact-info-panel">
          <div className="info-card">
            <div className="info-card-inner">
              <div className="contact-logo">
                <div className="logo-icon">
                  <span className="logo-symbol">$</span>
                </div>
                <h2>Adelphi Finance Club</h2>
              </div>
              
              <p className="contact-intro">
                We'd love to hear from you! Whether you're interested in joining the club, have questions about our events, or want to collaborate, please reach out using any of the methods below.
              </p>
              
              <div className="contact-details">
                <div className="contact-method">
                  <div className="method-icon location"></div>
                  <div className="method-details">
                    <h3>Visit Us</h3>
                    <p>Adelphi University<br />Robert B. Willumstad School of Business<br />1 South Ave, Garden City, NY 11530</p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="method-icon email"></div>
                  <div className="method-details">
                    <h3>Email Us</h3>
                    <p><a href="mailto:financeclub@adelphi.edu">financeclub@adelphi.edu</a></p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="method-icon meeting"></div>
                  <div className="method-details">
                    <h3>Meet Us</h3>
                    <p>Meetings: Wednesdays at 5:30 PM<br />Business Building, Room 215</p>
                  </div>
                </div>
              </div>
              
              <div className="social-section">
                <h3>Connect With Us</h3>
                <div className="social-links">
                  <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="social-link linkedin"></a>
                  <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="social-link instagram"></a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="social-link twitter"></a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="contact-form-panel">
          <div className="form-card">
            <div className="form-header">
              <h2>Send Us a Message</h2>
              <p>Fill out the form below and we'll get back to you as soon as possible.</p>
            </div>
            
            {formStatus.submitted ? (
              <div className="form-success">
                <div className="success-icon">✓</div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out to us. We'll respond to your inquiry shortly.</p>
                <button 
                  className="reset-button"
                  onClick={() => setFormStatus({ submitted: false, error: false, message: '' })}
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form ref={form} onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe" 
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Your Email</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com" 
                      required 
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <select 
                    id="subject" 
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a topic</option>
                    <option value="Membership Information">Membership Information</option>
                    <option value="Events & Programs">Events & Programs</option>
                    <option value="Partnership Opportunities">Partnership Opportunities</option>
                    <option value="Other Inquiry">Other Inquiry</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Your Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help you?" 
                    rows={6} 
                    required
                  ></textarea>
                </div>
                
                {formStatus.error && (
                  <div className="form-error">
                    <p>{formStatus.message}</p>
                  </div>
                )}
                
                <div className="form-actions">
                  <button type="submit" className="submit-button">
                    <span className="button-text">Send Message</span>
                    <span className="button-icon">→</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      
      <div className="map-section">
        <div className="map-card">
          <div className="map-header">
            <h2>Find Us on Campus</h2>
          </div>
          <div className="map-container">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.1506116187757!2d-73.6521629!3d40.7197877!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c27d9747cb08c7%3A0x3701d4bd1faec394!2sAdelph-New%20York%2C%20United%20States!5e0!3m2!1sen!2sus!4v1693949799085!5m2!1sen!2sus" 
              width="600" 
              height="450" 
              style={{ border: 0 }} 
              allowFullScreen 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Adelphi University Campus Map"
            ></iframe>
          </div>
        </div>
      </div>
      
      <div className="faq-section">
        <div className="faq-container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How do I join the Finance Club?</h3>
              <p>You can join by attending any of our meetings, which are held every Wednesday at 5:30 PM in Business Building, Room 215. Alternatively, you can register through the <a href="https://myaulife.adelphi.edu/organization/financeclub/" target="_blank" rel="noopener noreferrer">AULife Finance Club page</a>.</p>
            </div>
            
            <div className="faq-item">
              <h3>Are there membership fees?</h3>
              <p>No, membership is free for all Adelphi University students.</p>
            </div>
            
            <div className="faq-item">
              <h3>Do I need to be a finance major to join?</h3>
              <p>Not at all! While many of our members are finance majors, we welcome students from all academic backgrounds who are interested in learning about finance and business.</p>
            </div>
            
            <div className="faq-item">
              <h3>What types of events do you host?</h3>
              <p>We host a variety of events including guest speaker sessions, workshops, networking events, Bloomberg Terminal training, and field trips to financial institutions in NYC.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;